/**
 * Michigan County Code Utilities
 * Handles extraction and validation of 2-digit county codes in APNs
 */

// Complete Michigan County Code Map (Alphabetical 01-83)
export const MICHIGAN_COUNTY_CODES = {
  'Alcona': '01',
  'Alger': '02',
  'Allegan': '03',
  'Alpena': '04',
  'Antrim': '05',
  'Arenac': '06',
  'Baraga': '07',
  'Barry': '08',
  'Bay': '09',
  'Benzie': '10',
  'Berrien': '11',
  'Branch': '12',
  'Calhoun': '13',
  'Cass': '14',
  'Charlevoix': '15',
  'Cheboygan': '16',
  'Chippewa': '17',
  'Clare': '18',
  'Clinton': '19',
  'Crawford': '20',
  'Delta': '21',
  'Dickinson': '22',
  'Eaton': '23',
  'Emmet': '24',
  'Genesee': '25',
  'Gladwin': '26',
  'Gogebic': '27',
  'Grand Traverse': '28',
  'Gratiot': '29',
  'Hillsdale': '30',
  'Houghton': '31',
  'Huron': '32',
  'Ingham': '33',
  'Ionia': '34',
  'Iosco': '35',
  'Iron': '36',
  'Isabella': '37',
  'Jackson': '38',
  'Kalamazoo': '39',
  'Kalkaska': '40',
  'Kent': '41',
  'Keweenaw': '42',
  'Lake': '43',
  'Lapeer': '44',
  'Leelanau': '45',
  'Lenawee': '46',
  'Livingston': '47',
  'Luce': '48',
  'Mackinac': '49',
  'Macomb': '50',
  'Manistee': '51',
  'Marquette': '52',
  'Mason': '53',
  'Mecosta': '54',
  'Menominee': '55',
  'Midland': '56',
  'Missaukee': '57',
  'Monroe': '58',
  'Montcalm': '59',
  'Montmorency': '60',
  'Muskegon': '61',
  'Newaygo': '62',
  'Oakland': '63',
  'Oceana': '64',
  'Ogemaw': '65',
  'Ontonagon': '66',
  'Osceola': '67',
  'Oscoda': '68',
  'Otsego': '69',
  'Ottawa': '70',
  'Presque Isle': '71',
  'Roscommon': '72',
  'Saginaw': '73',
  'St. Clair': '74',
  'St. Joseph': '75',
  'Sanilac': '76',
  'Schoolcraft': '77',
  'Shiawassee': '78',
  'Tuscola': '79',
  'Van Buren': '80',
  'Washtenaw': '81',
  'Wayne': '82',
  'Wexford': '83'
};

// Reverse map: code to county name
export const COUNTY_CODE_TO_NAME = Object.fromEntries(
  Object.entries(MICHIGAN_COUNTY_CODES).map(([name, code]) => [code, name])
);

/**
 * Extract county code from APN
 * @param {string} apn - The APN/Parcel ID
 * @returns {string|null} - Two-digit county code or null if not found
 */
export const extractCountyCodeFromAPN = (apn) => {
  if (!apn || typeof apn !== 'string') {
    return null;
  }

  // Remove any whitespace
  const cleanApn = apn.trim();

  // Check if APN starts with a 2-digit number
  const match = cleanApn.match(/^(\d{2})/);

  if (match) {
    const code = match[1];
    // Validate it's a valid Michigan county code (01-83)
    if (COUNTY_CODE_TO_NAME[code]) {
      return code;
    }
  }

  return null;
};

/**
 * Check if APN already has county code prefix
 * @param {string} apn - The APN/Parcel ID
 * @param {string} countyName - The county name
 * @returns {boolean} - True if APN has correct county code prefix
 */
export const hasCountyCodePrefix = (apn, countyName) => {
  if (!apn || !countyName) {
    return false;
  }

  const expectedCode = MICHIGAN_COUNTY_CODES[countyName];
  if (!expectedCode) {
    return false;
  }

  const extractedCode = extractCountyCodeFromAPN(apn);
  return extractedCode === expectedCode;
};

/**
 * Prepend county code to APN if missing
 * @param {string} apn - The APN/Parcel ID
 * @param {string} countyName - The county name
 * @returns {string} - APN with county code prefix
 */
export const ensureCountyCodePrefix = (apn, countyName) => {
  if (!apn) {
    return apn;
  }

  // Get county code for this county
  const countyCode = MICHIGAN_COUNTY_CODES[countyName];
  if (!countyCode) {
    console.warn(`Unknown county: ${countyName}. Cannot add county code to APN.`);
    return apn;
  }

  // Clean the APN
  const cleanApn = apn.trim();

  // Check if it already has the correct county code
  if (hasCountyCodePrefix(cleanApn, countyName)) {
    return cleanApn;
  }

  // Always prepend county code (even if first 2 digits match a different county code)
  // The first 2 digits matching a county code is usually just coincidental
  const existingCode = extractCountyCodeFromAPN(cleanApn);
  if (existingCode && existingCode !== countyCode) {
    console.log(
      `APN "${cleanApn}" starts with "${existingCode}" which matches ${COUNTY_CODE_TO_NAME[existingCode]}, ` +
      `but prepending correct county code "${countyCode}" for ${countyName}.`
    );
  }

  // Prepend county code
  // Handle both dash-separated (41-13-21-328-029) and non-dash formats
  const separator = cleanApn.includes('-') ? '-' : '';
  return `${countyCode}${separator}${cleanApn}`;
};

/**
 * Get county name from APN
 * @param {string} apn - The APN/Parcel ID
 * @returns {string|null} - County name or null if not found
 */
export const getCountyNameFromAPN = (apn) => {
  const code = extractCountyCodeFromAPN(apn);
  if (!code) {
    return null;
  }
  return COUNTY_CODE_TO_NAME[code] || null;
};

/**
 * Validate APN format and county code
 * @param {string} apn - The APN/Parcel ID
 * @param {string} countyName - Expected county name (optional)
 * @returns {object} - Validation result with { valid, message, extractedCounty, extractedCode }
 */
export const validateAPN = (apn, countyName = null) => {
  if (!apn || typeof apn !== 'string') {
    return {
      valid: false,
      message: 'APN is required',
      extractedCounty: null,
      extractedCode: null
    };
  }

  const cleanApn = apn.trim();
  const extractedCode = extractCountyCodeFromAPN(cleanApn);

  if (!extractedCode) {
    return {
      valid: false,
      message: 'APN does not have a valid Michigan county code prefix',
      extractedCounty: null,
      extractedCode: null
    };
  }

  const extractedCounty = COUNTY_CODE_TO_NAME[extractedCode];

  // If county name is provided, validate it matches
  if (countyName) {
    const expectedCode = MICHIGAN_COUNTY_CODES[countyName];
    if (extractedCode !== expectedCode) {
      return {
        valid: false,
        message: `APN county code "${extractedCode}" (${extractedCounty}) does not match expected county "${countyName}" (code "${expectedCode}")`,
        extractedCounty,
        extractedCode
      };
    }
  }

  return {
    valid: true,
    message: 'APN has valid county code prefix',
    extractedCounty,
    extractedCode
  };
};
